import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash, FaArrowLeft } from "react-icons/fa";
import { Button } from "../../NextComponents/Button";
import { TextField } from "../../NextComponents/Fields";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import { baseUrl } from "../../../Environments/environment";
import onelyk from "../../../Assets/onelyklogo.svg";
import { requestHandler } from "../../../utils";
import { LocalStorage } from "../../../utils";
import { apiClient, postGoogleAuth } from "../../../api";
import UserValidationSchema from "../../../utils/userValidation";
import { z } from "zod";
import AuthOrDivider from "../AuthOrDivider";
import GoogleAuthButton from "../GoogleAuthButton";
import ConditionsModal from "../../Conditions/ConditionsModal";
import ReCaptchaField from "../../Common/ReCaptchaField";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../../../Contexts/AuthContext/AuthContext";
import { useGoogleLogin, useGoogleOAuth } from "@react-oauth/google";

export default function Register() {
	const [formData, setFormData] = useState({
		name: "",
		email: "",
		password: "",
	});
	const [otp, setOtp] = useState("");
	const [isRegistering, setIsRegistering] = useState(true);
	const [verified, setVerified] = useState(false);
	const router = useNavigate();
	const [isLoading, setIsLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [resendCountdown, setResendCountdown] = useState(0);
	const [inputErrors, setInputErrors] = useState({});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalContent, setModalContent] = useState("");
	const [isChecked, setIsChecked] = useState(false);
	const [captchaToken, setCaptchaToken] = useState(null);
	const { syncAuthFromStorage } = useAuth();
	const [googleLoading, setGoogleLoading] = useState(false);
	const [signupStep, setSignupStep] = useState("form");
	const [googlePasswordForm, setGooglePasswordForm] = useState({
		password: "",
		confirmPassword: "",
	});
	const [googleSignupProfile, setGoogleSignupProfile] = useState(null);
	const { scriptLoadedSuccessfully } = useGoogleOAuth();

	const resetGoogleSignup = () => {
		setGoogleSignupProfile(null);
		setGooglePasswordForm({ password: "", confirmPassword: "" });
		setSignupStep("form");
		setIsRegistering(true);
		setInputErrors({});
	};

	const GooglePasswordSchema = z
		.object({
			password: UserValidationSchema.shape.password,
			confirmPassword: z.string().min(1, "Please confirm your password"),
		})
		.refine((data) => data.password === data.confirmPassword, {
			message: "Passwords do not match",
			path: ["confirmPassword"],
		});

	const persistGoogleSession = (result) => {
		LocalStorage.set("accessToken", result.accessToken);
		LocalStorage.set("user", JSON.stringify(result.user));
		syncAuthFromStorage();
	};

	const handleGoogleSignupCheck = async (code) => {
		setGoogleLoading(true);
		setIsLoading(true);
		setInputErrors({});
		try {
			const response = await postGoogleAuth({ code, intent: "check" });
			const { result } = response.data ?? {};

			if (result?.needsPasswordSetup) {
				const profile = {
					name: result.name ?? result.user?.name ?? "",
					email: result.email ?? result.user?.email ?? "",
					googleId: result.googleId ?? result.user?.googleId ?? "",
				};

				if (!profile.googleId || !profile.email) {
					toast.error("Invalid response from Google sign-in");
					return;
				}

				setGoogleSignupProfile(profile);
				setSignupStep("google-password");
				setIsRegistering(false);
				setInputErrors({});
				return;
			}

			if (!result?.accessToken) {
				toast.error("Invalid response from Google sign-in");
				return;
			}

			persistGoogleSession(result);
			toast.success("Welcome back! You're signed in.");
			router("/propertyhome", { replace: true });
		} catch (error) {
			const message =
				error?.response?.data?.message ||
				"Google sign-up failed. Please try again.";
			toast.error(message);
		} finally {
			setGoogleLoading(false);
			setIsLoading(false);
		}
	};

	const startGooglePopup = useGoogleLogin({
		flow: "auth-code",
		ux_mode: "popup",
		onSuccess: (codeResponse) => {
			handleGoogleSignupCheck(codeResponse.code);
		},
		onError: () => {
			toast.error("Google sign-in was not authorized.");
			setGoogleLoading(false);
		},
		onNonOAuthError: () => {
			setGoogleLoading(false);
		},
	});

	const handleGooglePasswordChange = (e) => {
		const { name, value } = e.target;
		setGooglePasswordForm((prev) => ({ ...prev, [name]: value }));
	};

	const handleGooglePasswordSubmit = async (e) => {
		e.preventDefault();

		if (!googleSignupProfile?.googleId || !googleSignupProfile?.email) {
			toast.error(
				"Google sign-up session expired. Please sign up with Google again.",
			);
			resetGoogleSignup();
			return;
		}

		if (!isChecked) {
			setInputErrors({
				termsAccepted: "You must accept the terms and privacy policy.",
			});
			return;
		}

		const validation = GooglePasswordSchema.safeParse(googlePasswordForm);
		if (!validation.success) {
			const errors = {};
			validation.error.errors.forEach((err) => {
				errors[err.path[0]] = err.message;
			});
			setInputErrors(errors);
			return;
		}

		setIsLoading(true);
		setInputErrors({});
		try {
			const response = await postGoogleAuth({
				name: googleSignupProfile.name,
				email: googleSignupProfile.email,
				googleId: googleSignupProfile.googleId,
				password: googlePasswordForm.password,
				confirmPassword: googlePasswordForm.confirmPassword,
				intent: "signup",
			});
			const { result } = response.data ?? {};

			if (!result?.accessToken) {
				toast.error("Invalid response from Google sign-in");
				return;
			}

			persistGoogleSession(result);
			setGoogleSignupProfile(null);
			setGooglePasswordForm({ password: "", confirmPassword: "" });
			toast.success("Account created successfully!");
			router("/propertyhome", { replace: true });
		} catch (error) {
			const message =
				error?.response?.data?.message ||
				"Google sign-up failed. Please try again.";
			toast.error(message);
		} finally {
			setIsLoading(false);
		}
	};

	const handleGoogleClick = () => {
		if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
			toast.info(
				"Add REACT_APP_GOOGLE_CLIENT_ID to enable Google popup sign-in. Your backend should accept POST /api/auth/google.",
			);
			return;
		}
		if (!scriptLoadedSuccessfully) {
			toast.warning(
				"Google sign-in is still loading. Try again in a moment.",
			);
			return;
		}
		setGoogleLoading(true);
		startGooglePopup();
	};

	useEffect(() => {
		let timer;
		if (resendCountdown > 0) {
			timer = setTimeout(() => {
				setResendCountdown(resendCountdown - 1);
			}, 1000);
		}
		return () => clearTimeout(timer);
	}, [resendCountdown]);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value,
		});
	};

	const handleOtpChange = (e) => {
		const { value } = e.target;
		setOtp(value);
	};

	const handleRegisterSubmit = async (e) => {
		e.preventDefault();
		if (!captchaToken) {
			setInputErrors({ captcha: "Please verify captcha" });
			return;
		}
		setIsLoading(true);

		const dataToSend = {
			name: formData.name,
			email: formData.email,
			password: formData.password,
			captchaToken,
		};

		const validationResult = UserValidationSchema.safeParse({
			name: formData.name,
			email: formData.email,
			password: formData.password,
		});

		let errors = {};

		if (!validationResult.success) {
			validationResult.error.errors.forEach((err) => {
				errors[err.path[0]] = err.message;
			});
		}
		if (!isChecked) {
			errors["termsAccepted"] =
				"You must accept the terms and privacy policy.";
		}

		if (Object.keys(errors).length > 0) {
			setInputErrors(errors);
			setIsLoading(false);
			return;
		}

		requestHandler(
			async () => await apiClient.post(`${baseUrl}/user/register`, dataToSend),
			setIsLoading,
			() => {
				setIsRegistering(false);
				setSignupStep("otp");
				toast.success("Otp send to your email!");
				setResendCountdown(60);
				setInputErrors({});
			},
			(err) => toast.error(err),
		);
	};

	const handleResendOtp = async () => {
		setIsLoading(true);

		try {
			await apiClient.post(`${baseUrl}/auth/resendOtp`, formData);
			toast.success("OTP resent successfully!");
			setResendCountdown(60);
		} catch (error) {
			const errorMessage =
				error.response?.data?.message ||
				"Failed to resend OTP. Please try again.";
			console.error(errorMessage);
			toast.error(errorMessage);
		} finally {
			setIsLoading(false);
		}
	};

	const handleOtpSubmit = async (e) => {
		e.preventDefault();
		setIsLoading(true);

		try {
			const response = await apiClient.post(`${baseUrl}/auth/verifyOtp`, {
				email: formData.email,
				otp,
			});

			const result = response?.data?.result || {};
			const accessToken = result.accessToken || response?.data?.accessToken;
			const userFromResponse = result.user || response?.data?.user;

			if (!accessToken) {
				throw new Error("Access token not found in OTP verify response");
			}

			LocalStorage.set("accessToken", accessToken);

			if (userFromResponse) {
				LocalStorage.set("user", JSON.stringify(userFromResponse));
			} else {
				// Build a minimal user object from token claims so protected routes work.
				const decoded = jwtDecode(accessToken);
				LocalStorage.set(
					"user",
					JSON.stringify({
						_id: decoded?._id || decoded?.id || decoded?.userId || decoded?.sub,
						role: decoded?.role,
					}),
				);
			}

			syncAuthFromStorage();

			setVerified(true);
			toast.success("OTP verified. You are now logged in.");
			router("/propertyhome", { replace: true });
		} catch (error) {
			console.error(error);
			toast.error("Wrong Otp!");
		} finally {
			setIsLoading(false);
		}
	};

	const openModal = (type) => {
		if (type === "privacy") {
			setModalContent("privacy");
		} else if (type === "terms") {
			setModalContent("terms");
		}
		console.log(type);
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
		setModalContent("");
	};

	return (
		<div className="relative overflow-hidden bg-gray-800 min-h-screen">
			<div aria-hidden="true" className="hidden sm:absolute sm:inset-0 sm:block">
				<svg
					fill="none"
					width={364}
					height={384}
					viewBox="0 0 364 384"
					className="absolute bottom-0 right-0 mb-48 translate-x-1/2 transform text-gray-700 lg:top-0 lg:mb-0 lg:mt-28 xl:translate-x-0 xl:transform-none"
				>
					<defs>
						<pattern
							x={0}
							y={0}
							id="register-pattern"
							width={20}
							height={20}
							patternUnits="userSpaceOnUse"
						>
							<rect x={0} y={0} fill="currentColor" width={4} height={4} />
						</pattern>
					</defs>
					<rect fill="url(#register-pattern)" width={364} height={384} />
				</svg>
			</div>

			{isLoading && <LoadingSpinner />}
			<div className="relative z-10 flex min-h-screen flex-col">
				<div className="mx-auto flex w-full max-w-7xl items-center justify-between px-6 pt-6">
					<Link to="/" aria-label="Home">
						<img src={onelyk} alt="Home" className="h-8 w-auto sm:h-10" />
					</Link>
				</div>

				<div className="flex flex-1 items-center justify-center px-4 py-10 sm:px-6 lg:px-8">
					<div className="flex w-full max-w-[22rem] flex-col items-center">
						<div className="grid w-full grid-cols-[1fr_auto_1fr] items-center gap-2">
							<div className="flex justify-start">
								{((!isRegistering && !verified && signupStep === "otp") ||
									signupStep === "google-password") && (
									<button
										type="button"
										onClick={() => {
											if (signupStep === "google-password") {
												resetGoogleSignup();
												return;
											}
											setIsRegistering(true);
											setSignupStep("form");
										}}
										className="flex items-center gap-2 text-sm text-gray-200 hover:text-white"
									>
										<FaArrowLeft />
										Back
									</button>
								)}
							</div>
							<div aria-hidden="true" />
							<div aria-hidden="true" />
						</div>

						{signupStep === "google-password" ? (
							<div className="mt-8 w-full border border-gray-200/90 bg-white px-5 py-7 shadow-sm sm:px-6">
								<h2 className="text-center text-lg font-semibold tracking-tight text-gray-900">
									Create a password
								</h2>
								<p className="mt-2 text-center text-sm text-gray-600">
									{googleSignupProfile?.email ? (
										<>
											Signing up as{" "}
											<span className="font-medium">
												{googleSignupProfile.email}
											</span>
											. Create a password to finish signup and sign in with Google
											or email next time.
										</>
									) : (
										"Google sign-in succeeded. Create a password to finish signup and sign in with Google or email next time."
									)}
								</p>
								<form
									onSubmit={handleGooglePasswordSubmit}
									className="mt-7 grid grid-cols-1 gap-y-5"
								>
									<div>
										<div className="relative">
											<TextField
												label="Password"
												name="password"
												type={showPassword ? "text" : "password"}
												autoComplete="new-password"
												required
												onChange={handleGooglePasswordChange}
												value={googlePasswordForm.password}
											/>
											<button
												type="button"
												className="absolute right-0 top-10 flex items-center pr-3 text-sm"
												onClick={() => setShowPassword(!showPassword)}
												aria-label={
													showPassword ? "Hide password" : "Show password"
												}
											>
												{showPassword ? (
													<FaEyeSlash className="h-5 w-5 text-gray-500" />
												) : (
													<FaEye className="h-5 w-5 text-gray-500" />
												)}
											</button>
										</div>
										<div className="text-xs text-red-500">
											{inputErrors.password && (
												<span>{inputErrors.password}</span>
											)}
										</div>
									</div>
									<div>
										<TextField
											label="Confirm password"
											name="confirmPassword"
											type={showPassword ? "text" : "password"}
											autoComplete="new-password"
											required
											onChange={handleGooglePasswordChange}
											value={googlePasswordForm.confirmPassword}
										/>
										<div className="text-xs text-red-500">
											{inputErrors.confirmPassword && (
												<span>{inputErrors.confirmPassword}</span>
											)}
										</div>
									</div>
									<div>
										<label className="flex items-start gap-2 text-sm text-gray-700">
											<input
												type="checkbox"
												className="mt-0.5"
												checked={isChecked}
												onChange={(e) => setIsChecked(e.target.checked)}
												required
											/>
											<span className="leading-snug">
												I agree to the{" "}
												<button
													type="button"
													onClick={() => openModal("terms")}
													className="text-blue-600 underline hover:text-blue-700"
												>
													Terms and Conditions
												</button>{" "}
												and{" "}
												<button
													type="button"
													onClick={() => openModal("privacy")}
													className="text-blue-600 underline hover:text-blue-700"
												>
													Privacy Policy
												</button>
											</span>
										</label>
										{inputErrors.termsAccepted && (
											<div className="mt-1 text-xs text-red-500">
												{inputErrors.termsAccepted}
											</div>
										)}
									</div>
									<div>
										<Button
											type="submit"
											variant="solid"
											color="blue"
											className="w-full"
											disabled={isLoading}
										>
											Complete sign up
										</Button>
									</div>
								</form>
							</div>
						) : isRegistering ? (
							<div className="mt-8 w-full border border-gray-200/90 bg-white px-5 py-7 shadow-sm sm:px-6">
								<h2 className="text-center text-lg font-semibold tracking-tight text-gray-900">
									Get started for free
								</h2>
								<p className="mt-2 text-center text-sm text-gray-600">
									Already registered?{" "}
									<Link to="/" className="font-medium text-blue-600 hover:underline">
										Sign in
									</Link>
								</p>
								<form
									onSubmit={handleRegisterSubmit}
									className="mt-7 grid grid-cols-1 gap-y-5"
								>
									<div>
										<TextField
											label="Name"
											name="name"
											type="text"
											autoComplete="name"
											required
											onChange={handleChange}
											value={formData.name}
										/>
										<div className="text-xs text-red-500">
											{inputErrors.name && <span>{inputErrors.name}</span>}
										</div>
									</div>

									<div>
										<TextField
											label="Email address"
											name="email"
											type="email"
											autoComplete="email"
											required
											onChange={handleChange}
											value={formData.email}
										/>
										<div className="text-xs text-red-500">
											{inputErrors.email && <span>{inputErrors.email}</span>}
										</div>
									</div>

									{/* Phone number — restore when moved back from property onboard */}
									{/*
							<div>
								<label className="mb-2 block text-sm font-medium text-gray-700">
									Phone Number
								</label>
								... country + tel input ...
							</div>
							*/}

									<div>
										<div className="relative">
											<TextField
												label="Password"
												name="password"
												type={showPassword ? "text" : "password"}
												autoComplete="new-password"
												required
												onChange={handleChange}
												value={formData.password}
											/>
											<button
												type="button"
												className="absolute right-0 top-10 flex items-center pr-3 text-sm"
												onClick={() => setShowPassword(!showPassword)}
											>
												{showPassword ? (
													<FaEyeSlash className="h-5 w-5 text-gray-500" />
												) : (
													<FaEye className="h-5 w-5 text-gray-500" />
												)}
											</button>
										</div>
										<div className="text-xs text-red-500">
											{inputErrors.password && (
												<span>{inputErrors.password}</span>
											)}
										</div>
									</div>

									{/* How did you hear about us — moving to property onboard */}
									{/*
							<div>
								<SelectField
									label="How did you hear about us?"
									name="referralSource"
									required
									onChange={handleChange}
									value={formData.referralSource}
								>
									<option value="" disabled>Select an option</option>
									<option value="facebook">Facebook</option>
									<option value="twitter">Twitter</option>
									<option value="advertisement">Advertisement</option>
									<option value="other">Other</option>
								</SelectField>
							</div>
							*/}

									<div>
										<label className="flex items-start gap-2 text-sm text-gray-700">
											<input
												type="checkbox"
												className="mt-0.5"
												checked={isChecked}
												onChange={(e) => setIsChecked(e.target.checked)}
												required
											/>
											<span className="leading-snug">
												I agree to the{" "}
												<button
													type="button"
													onClick={() => openModal("terms")}
													className="text-blue-600 underline hover:text-blue-700"
												>
													Terms and Conditions
												</button>{" "}
												and{" "}
												<button
													type="button"
													onClick={() => openModal("privacy")}
													className="text-blue-600 underline hover:text-blue-700"
												>
													Privacy Policy
												</button>
											</span>
										</label>
										{inputErrors.termsAccepted && (
											<div className="mt-1 text-xs text-red-500">
												{inputErrors.termsAccepted}
											</div>
										)}
									</div>

									<div className="flex flex-col items-stretch">
										<ReCaptchaField
											onVerify={(token) => {
												setCaptchaToken(token);

												if (token) {
													setInputErrors((prev) => ({
														...prev,
														captcha: null,
													}));
												}
											}}
										/>
										<div className="mt-1 text-center text-xs text-red-500">
											{inputErrors.captcha && <span>{inputErrors.captcha}</span>}
										</div>
									</div>

									<div>
										<Button
											type="submit"
											variant="solid"
											color="blue"
											className="w-full"
											disabled={googleLoading}
										>
											<span>
												Sign up <span aria-hidden="true">&rarr;</span>
											</span>
										</Button>
									</div>
									<div>
										<AuthOrDivider />
										<GoogleAuthButton
											type="button"
											loading={googleLoading}
											disabled={isLoading}
											onClick={handleGoogleClick}
										/>
									</div>
								</form>
							</div>
						) : (
							<div className="mt-8 w-full border border-gray-200/90 bg-white px-5 py-7 shadow-sm sm:px-6">
								{!verified ? (
									<form
										onSubmit={handleOtpSubmit}
										className="grid grid-cols-1 gap-y-5"
									>
										<div>
											<h2 className="text-lg font-semibold text-gray-900">
												Verify your email
											</h2>
											<p className="mt-2 text-sm text-gray-600">
												Enter the OTP sent to your email.
											</p>
										</div>
										<TextField
											label="OTP"
											name="otp"
											type="text"
											required
											onChange={handleOtpChange}
											value={otp}
										/>
										<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
											<Button
												type="submit"
												variant="solid"
												color="blue"
												className="w-full sm:w-auto"
											>
												<span>
													Verify <span aria-hidden="true">&rarr;</span>
												</span>
											</Button>
											<Button
												type="button"
												variant="outline"
												color="gray"
												className="w-full sm:w-auto"
												onClick={handleResendOtp}
												disabled={resendCountdown > 0}
											>
												{resendCountdown > 0
													? `Resend OTP (${resendCountdown})`
													: "Resend OTP"}
											</Button>
										</div>
									</form>
								) : (
									<div className="text-center">
										<h3 className="text-lg font-semibold text-green-600">
											Email verified successfully!
										</h3>
										<p className="mt-2 text-sm text-gray-600">
											Your email has been verified. Redirecting to login page...
										</p>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			</div>
			<ConditionsModal
				isOpen={isModalOpen}
				onClose={closeModal}
				type={modalContent}
			/>
		</div>
	);
}
